




































































































































import Component from 'vue-class-component'
import Vue from 'vue'
import axios from 'axios'
import jwt from 'jsonwebtoken'
import { isEmpty } from 'lodash'

import { TextFieldRules, SocialLoginType } from '@/utils/enums'
import eventModule from '@/store/event'
import authModule from '@/store/auth'
import userModule from '@/store/user'
import PasswordReset from '@/components/user/profile/PasswordReset.vue'
import OptionalSettingsDialog from '@/components/user/OptionalSettingsDialog.vue'
import MessageAlert from '@/components/MessageAlert.vue'

@Component({
  name: 'LoginView',
  components: {
    PasswordReset,
    OptionalSettingsDialog,
    MessageAlert
  }
})
class LoginView extends Vue {
  private isFormValid = false
  private showNamePrompt = false
  private showOptionals = false
  private email = ''
  private firstName = ''
  private googleAccessToken = ''
  private fbAccessToken = ''
  private appleUserId = ''
  private appleCode = ''
  private rules = TextFieldRules
  private socialLoginMethod = ''
  private form = {
    email: '',
    password: ''
  }

  private async login() {
    await authModule.login(this.form)
    this.routeToHome()
  }

  private async created() {
    this.initializeSocialLogins()
  }

  mounted() {
    if (
      this.$route.name === 'PasswordReset' ||
      this.$route.name === 'NewPassword'
    ) {
      ;(this.$refs.passwordReset as PasswordReset).toggleDialog()
    }
  }

  private initializeSocialLogins() {
    const windowLocal = window as any
    windowLocal.AppleID.auth.init({
      clientId: process.env.VUE_APP_APPLE_CLIENT_ID,
      scope: 'email name',
      redirectURI: process.env.VUE_APP_HOME,
      state: 'init',
      usePopup: true // or false defaults to false
    })
    windowLocal.FB.init({
      appId: process.env.VUE_APP_FB_APP_ID,
      cookie: true,
      xfbml: true,
      version: 'v2.8'
    })
    windowLocal.gapi.load('auth2', () => {
      windowLocal.googleAuth2 = windowLocal.gapi.auth2.init({
        // eslint-disable-next-line @typescript-eslint/camelcase
        client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
        // eslint-disable-next-line @typescript-eslint/camelcase
        fetch_basic_profile: false,
        scope: 'profile email'
      })
    })
  }

  private register(): void {
    if (this.socialLoginMethod === SocialLoginType.APPLE) {
      this.registerApple()
    }
    if (this.socialLoginMethod === SocialLoginType.FACEBOOK) {
      this.registerFB()
    }
    if (this.socialLoginMethod === SocialLoginType.GOOGLE) {
      this.registerGoogle()
    }
  }

  private async loginApple(): Promise<void> {
    this.socialLoginMethod = SocialLoginType.APPLE
    const windowLocal = window as any

    try {
      const result = await windowLocal.AppleID.auth.signIn()
      const appleUser = jwt.decode(result.authorization.id_token, {
        complete: true
      }) as any
      this.appleUserId = appleUser.payload.sub
      this.email = appleUser.payload.email
      this.appleCode = result.authorization.code

      if (result.user && Object.hasOwnProperty.call(result.user, 'name')) {
        localStorage.setItem('profile', JSON.stringify(result.user))
        this.firstName = result.user.name.firstName
        this.registerApple()
      } else {
        const cachedProfile = JSON.parse(localStorage.getItem('profile')!)
        if (cachedProfile && !isEmpty(cachedProfile)) {
          this.firstName = cachedProfile.name.firstName
        }
        this.registerApple()
      }
    } catch (error) {
      eventModule.newError('Anmeldung mit Apple ID fehlgeschlagen')
    }
  }

  private async registerApple() {
    const authResponse = await axios.post(`/authenticate/appleid`, {
      firstName: this.firstName,
      email: this.email,
      userId: this.appleUserId,
      code: this.appleCode,
      web: true
    })
    const cachedProfile = localStorage.getItem('profile')
    if (cachedProfile) {
      localStorage.removeItem('profile')
    }
    this.setTokens(authResponse)
  }

  private loginGoogle(): void {
    this.socialLoginMethod = SocialLoginType.GOOGLE
    const windowLocal = window as any

    try {
      windowLocal.googleAuth2
        .signIn({
          prompt: 'select_account'
        })
        .then(async (result: any) => {
          this.googleAccessToken = result.getAuthResponse().id_token
          const GoogleUser = windowLocal.googleAuth2.currentUser.get() as any
          this.email = GoogleUser.getBasicProfile().getEmail()
          if (GoogleUser.getBasicProfile().getGivenName()) {
            this.firstName = GoogleUser.getBasicProfile().getGivenName()
            this.registerGoogle()
          } else {
            this.showNamePrompt = true
          }
        })
    } catch (error) {
      eventModule.newError('Anmeldung mit Google fehlgeschlagen')
    }
  }

  private async registerGoogle() {
    const authResponse = await axios.post(
      `/authenticate/google?access_token=${this.googleAccessToken}`,
      {
        firstName: this.firstName,
        email: this.email
      }
    )
    this.setTokens(authResponse)
  }

  private loginFacebook(): void {
    this.socialLoginMethod = SocialLoginType.FACEBOOK
    const windowLocal = window as any

    try {
      windowLocal.FB.login(
        (loginResponse: any) => {
          if (loginResponse.authResponse) {
            this.fbAccessToken = loginResponse.authResponse.accessToken
            windowLocal.FB.api(
              '/me',
              { fields: 'first_name,email' },
              async (graphResponse: any) => {
                this.email = graphResponse.email
                if (graphResponse.first_name) {
                  this.firstName = graphResponse.first_name
                  this.registerFB()
                } else {
                  this.showNamePrompt = true
                }
              }
            )
          }
        },
        { scope: 'email' }
      )
    } catch (error) {
      eventModule.newError('Anmeldung mit Facebook fehlgeschlagen')
    }
  }

  private async registerFB() {
    const authResponse = await axios.post(
      `/authenticate/facebook?access_token=${this.fbAccessToken}`,
      {
        firstName: this.firstName,
        email: this.email
      }
    )
    this.setTokens(authResponse)
  }

  private setTokens(authResponse: any) {
    // const bearerToken = 'bearer ' + authResponse.data.token
    const token = authResponse.data.token
    const refreshToken = authResponse.data.refreshToken
    const user = authResponse.data.user

    if (authResponse.data.success) {
      authModule.setTokens({ token, refreshToken })
      authModule.setUser(user)

      this.routeToHome()
    }
  }

  private async routeToHome(): Promise<void> {
    if (authModule.user) {
      if (
        // Update firstname if not set yet
        (!authModule.user.firstName ||
          authModule.user.firstName.trim().length === 0) &&
        this.firstName.trim().length > 0
      ) {
        await userModule.update({ firstName: this.firstName })
        this.firstName = ''
      }
      if (authModule.user.categories.length === 0) {
        // If user has no interests, user is new -> que the optionals
        this.showOptionals = true
      } else {
        this.$router.replace({ name: 'HomeView' })
      }
    }
  }

  private async routeToInterests(optionalsForm?: any): Promise<void> {
    if (optionalsForm) {
      await userModule.update(optionalsForm)
    }
    this.$router.replace({ name: 'Interests' })
  }

  private cancelLogin(): void {
    this.showNamePrompt = false
    authModule.logout()
  }
}

export default LoginView
